
import type { TenantPluginRegister } from '@yeshuman/sdk'
import { CandidatePreviewScoreAction } from '../../components/platform/candidate-preview-score-action'
import { registerDataBenchScoreActions } from '../../lib/data-bench-score-actions'
import {
  CandidateDashboard,
  EmployerDashboard,
  InterviewerDashboard,
  RecruiterDashboard,
} from './dashboards'
import { CandidateRoutes } from './routes/candidate-routes'
import { EmployerRoutes } from './routes/employer-routes'
import { InterviewerRoutes } from './routes/interviewer-routes'
import { RecruiterRoutes } from './routes/recruiter-routes'
import {
  SkillPoolCoverageReport,
  SkillPoolGlance,
} from '../../components/platform/skill-pool-coverage-page'
import { MarketplaceInterestPage } from '../../components/platform/marketplace-interest-page'
import { PlatformOpsPulsePage } from '../../components/platform/platform-ops-pulse-page'
import { PlatformActionsPage } from '../../components/platform/platform-actions-page'
import { PlatformJobPage } from '../../components/platform/platform-job-page'
import { PlatformJobsPage } from '../../components/platform/platform-jobs-page'
import { PlatformProfilesPage } from '../../components/platform/platform-profiles-page'
import { SharedAnalysisReportPage } from '../../components/public/shared-analysis-report-page'

export const register: TenantPluginRegister = (app) => {
  registerDataBenchScoreActions(app, CandidatePreviewScoreAction)
  app.focus.register({
    focus: 'candidate',
    home: CandidateDashboard,
    tree: CandidateRoutes,
    skeleton: 'candidate',
  })
  app.focus.register({
    focus: 'employer',
    home: EmployerDashboard,
    tree: EmployerRoutes,
    skeleton: 'employer',
  })
  app.focus.register({ focus: 'recruiter', home: RecruiterDashboard, tree: RecruiterRoutes })
  app.focus.register({ focus: 'interviewer', home: InterviewerDashboard, tree: InterviewerRoutes })

  app.routes.named('opportunities.manage', '/employer/jobs')
  app.routes.named('opportunity.manage', '/employer/jobs/:id')
  app.routes.named('organisation.opportunities', '/employer/organisations/:slug/jobs')
  app.routes.named('skills.poolCoverage', '/platform/skill-pool')
  app.routes.named('platform.opsPulse', '/platform/ops')
  app.routes.named('platform.marketplaceInterest', '/platform/marketplace-interest')
  app.routes.add({
    path: '/platform/ops',
    element: PlatformOpsPulsePage,
  })
  app.routes.add({
    path: '/platform/marketplace-interest',
    element: MarketplaceInterestPage,
  })
  app.routes.add({ path: '/platform/jobs', element: PlatformJobsPage })
  app.routes.add({ path: '/platform/jobs/:jobId', element: PlatformJobPage })
  app.routes.add({ path: '/platform/profiles', element: PlatformProfilesPage })
  app.routes.add({ path: '/platform/actions', element: PlatformActionsPage })
  app.publicShare.route({
    path: '/share/analysis/:token/people/:profileId',
    element: SharedAnalysisReportPage,
  })
  app.publicShare.route({
    path: '/share/analysis/:token',
    element: SharedAnalysisReportPage,
  })
  app.canvases.register({
    id: 'talentco.skill-pool',
    title: 'Skill pool',
    description: 'Published catalog skills against profiles in the talent pool.',
    targets: [{ page: '/platform/skills-catalog' }, { page: '/developer/skills-catalog' }],
    page: { path: '/platform/skill-pool' },
    glance: SkillPoolGlance,
    launcher: false,
    render: SkillPoolCoverageReport,
  })

  app.nav.add('candidate', [
    { path: '/candidate', icon: 'Home', exact: true, label: 'Home' },
    { path: '/profile', icon: 'User' },
    { path: '/candidate/jobs', icon: 'BriefcaseBusiness' },
  ])
  app.nav.add('employer', [
    { path: '/employer', icon: 'Home', exact: true, label: 'Home' },
    { path: '/employer/jobs', icon: 'BriefcaseBusiness', label: 'Job Openings' },
    { path: '/employer/organisations', icon: 'Building2' },
  ])
  app.nav.add('recruiter', [
    { path: '/recruiter', icon: 'Home', exact: true, label: 'Home' },
    { path: '/recruiter/jobs', icon: 'BriefcaseBusiness', label: 'Jobs' },
    { path: '/recruiter/briefs', icon: 'FileText', label: 'Briefs' },
    { path: '/recruiter/talent', icon: 'Search' },
  ])
  app.nav.add('interviewer', [
    { path: '/interviewer', icon: 'Home', exact: true, label: 'Home' },
    { path: '/interviewer/candidates', icon: 'Users', label: 'Candidates' },
    { path: '/interviewer/interviews', icon: 'CalendarCheck', label: 'Interviews' },
  ])
  app.nav.add('platform', [
    {
      path: '/platform/ops',
      icon: 'Activity',
      label: 'Platform pulse',
      after: '/platform/engagement',
    },
    {
      path: '/platform/marketplace-interest',
      icon: 'Heart',
      label: 'Marketplace interest',
      after: '/platform/ops',
    },
    {
      path: '/platform/skills-catalog',
      icon: 'BookOpen',
      label: 'Skill Lexicon',
      after: '/platform/classifiers',
    },
    {
      path: '/platform/skill-pool',
      icon: 'BarChart3',
      label: 'Skill pool',
      after: '/platform/skills-catalog',
    },
    {
      path: '/platform/jobs',
      icon: 'BriefcaseBusiness',
      label: 'Jobs',
      after: '/platform/organisations',
    },
    {
      path: '/platform/profiles',
      icon: 'Users',
      label: 'Profiles',
      after: '/platform/jobs',
    },
    {
      path: '/platform/actions',
      icon: 'ClipboardList',
      label: 'Action log',
      after: '/platform/profiles',
    },
    {
      path: '/platform/marketplace-ingest',
      icon: 'Globe2',
      requiresFeature: 'opportunityIngest',
      label: 'Marketplace ingest',
      after: '/platform/organisations',
    },
  ])
}

